import { LoadingOutlined } from '@ant-design/icons';
import { Statistic, theme } from 'antd';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { CSSProperties, ReactNode, useMemo } from 'react';
import useSingleCompanySetting from '../../hooks/adminPage/useSingleCompanySetting';
import useReports, { ReportData } from '../../hooks/dashboardPage/useReports';
import TimeDisplayForReport from '../TimeDisplayForReport';
import ReportCard from './ReportCard';

const styles = {
    valueStyle: { textAlign: 'center' } as CSSProperties
};

interface InfoBoxProps {
    icon?: ReactNode;
    /**
     * @refreshInterval number of seconds to refresh data in given prop (url)
     * @description it will only refresh data when the time passes, it will never refresh when 0 (only when re-rendered)
     */
    refreshInterval?: number;
    title: string;
    /**
     * @url the data will be loaded from this url
     */
    url: string;
    data?: ReportData;
}

export default function InfoBox({ icon, refreshInterval = 0, title, url, data = null }: InfoBoxProps) {
    const {
        token: { colorPrimary, fontSizeHeading3 }
    } = theme.useToken();
    const {
        query: { reportType }
    } = useRouter();
    const { data: zoneData } = useSingleCompanySetting('TIMEZONE');
    const zone = zoneData?.value;

    const reportUrl = data ? null : reportType ? url : zone ? `${url}?zone=${zone}` : null;

    const { data: reportData, isLoading, isValidating, mutate } = useReports<'box'>(reportUrl, refreshInterval);

    const infoBoxData = data ? data : reportData?.d?.[0];

    const updatedAt = useMemo(() => (infoBoxData && !isValidating ? Date.now() : null), [infoBoxData, isValidating]);

    const isTimeValue = infoBoxData?.t === 's';
    const isNumberValue = infoBoxData?.t === 'n';

    const suffixProp =
        !infoBoxData || isNumberValue
            ? {}
            : {
                  suffix: isTimeValue ? (
                      <TimeDisplayForReport
                          timeText={infoBoxData?.v?.toString()}
                          tooltipProps={{ placement: 'leftTop' }}
                      />
                  ) : (
                      '%'
                  )
              };

    return (
        <ReportCard loading={isLoading} title={title} updateTime={updatedAt} onButtonClick={!data ? mutate : null}>
            <Statistic
                className={clsx('box-report', (!infoBoxData || isTimeValue) && 'display-none')}
                value={infoBoxData?.v ?? ''}
                valueStyle={styles.valueStyle}
                precision={0}
                prefix={
                    <>
                        {isValidating && (
                            <LoadingOutlined style={{ color: colorPrimary, fontSize: fontSizeHeading3 }} />
                        )}
                        {icon}
                    </>
                }
                {...suffixProp}
            />
        </ReportCard>
    );
}
